import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { PageHeading, Alert, Spinner, Empty, StatusPill } from "../components/ui";

const TABS = ["Companies", "Positions", "Staff", "Housekeeping", "Audit"] as const;
type Tab = (typeof TABS)[number];

export default function Admin() {
  const [tab, setTab] = useState<Tab>("Companies");
  return (
    <>
      <PageHeading title="Coordinator admin" />
      <div role="tablist" aria-label="Admin sections" className="mb-6 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={tab === t ? "btn-primary" : "btn-secondary"}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "Companies" && <Companies />}
      {tab === "Positions" && <PositionApprovals />}
      {tab === "Staff" && <Staff />}
      {tab === "Housekeeping" && <Housekeeping />}
      {tab === "Audit" && <Audit />}
    </>
  );
}

function useDecision(path: string, keys: string[][]) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api.post(`${path}/${id}/decision`, { approve }),
    onSuccess: () => keys.forEach((key) => queryClient.invalidateQueries({ queryKey: key })),
  });
}

function Companies() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "companies"],
    queryFn: () => api.get<{ companies: any[] }>("/admin/companies"),
  });
  const decide = useDecision("/admin/companies", [["admin", "companies"], ["admin", "overview"]]);

  if (isLoading) return <Spinner />;
  const companies = data?.companies ?? [];
  if (companies.length === 0) return <Empty>No organisations have registered yet.</Empty>;

  return (
    <ul className="space-y-3">
      {companies.map((c) => (
        <li key={c.id} className="card flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-heading">{c.name}</p>
            <p className="text-sm text-uoa-grey">
              {c.contactName} · {c.contactEmail}
              {c.website ? ` · ${c.website}` : ""} · {c._count.positions} position{c._count.positions === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={c.status} />
            {c.status !== "APPROVED" && (
              <button className="btn-primary" onClick={() => decide.mutate({ id: c.id, approve: true })}>Approve</button>
            )}
            {c.status !== "REJECTED" && (
              <button className="btn-secondary" onClick={() => decide.mutate({ id: c.id, approve: false })}>Reject</button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function PositionApprovals() {
  const { data, isLoading } = useQuery({
    queryKey: ["positions"],
    queryFn: () => api.get<{ positions: any[] }>("/positions"),
  });
  const decide = useDecision("/admin/positions", [["positions"], ["admin", "overview"]]);

  if (isLoading) return <Spinner />;
  const pending = data?.positions.filter((p) => p.status === "PENDING_APPROVAL") ?? [];
  if (pending.length === 0) return <Empty>No positions are awaiting approval.</Empty>;

  return (
    <ul className="space-y-3">
      {pending.map((p) => (
        <li key={p.id} className="card">
          <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-heading">{p.title}</p>
              <p className="text-sm text-uoa-grey">{p.company.name}{p.location ? ` · ${p.location}` : ""}</p>
            </div>
            <StatusPill status={p.status} />
          </div>
          <p className="mb-4 whitespace-pre-wrap text-sm">{p.description}</p>
          <div className="flex gap-3">
            <button className="btn-primary" onClick={() => decide.mutate({ id: p.id, approve: true })}>Publish</button>
            <button className="btn-secondary" onClick={() => decide.mutate({ id: p.id, approve: false })}>Send back as draft</button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Staff() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "staff"],
    queryFn: () => api.get<{ staff: any[] }>("/admin/staff"),
  });

  const add = useMutation({
    mutationFn: (body: unknown) => api.post("/admin/staff", body),
    onSuccess: () => { setError(null); queryClient.invalidateQueries({ queryKey: ["admin", "staff"] }); },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not add staff member"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/admin/staff/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "staff"] }),
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <Alert>
        This directory is what grants the staff role. Students and staff share the same email domain, so anyone not
        listed here signs in as a student.
      </Alert>
      {error && <Alert tone="error">{error}</Alert>}

      <form
        className="card mb-6 grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          add.mutate([{
            email: f.get("email"), name: f.get("name"),
            department: f.get("department") || undefined, title: f.get("title") || undefined,
            coordinator: f.get("coordinator") === "on",
          }]);
          e.currentTarget.reset();
        }}
      >
        <div><label className="label" htmlFor="name">Name</label><input id="name" name="name" required className="input" /></div>
        <div><label className="label" htmlFor="email">University email</label><input id="email" name="email" type="email" required className="input" /></div>
        <div><label className="label" htmlFor="department">Department</label><input id="department" name="department" className="input" /></div>
        <div><label className="label" htmlFor="title">Title</label><input id="title" name="title" className="input" /></div>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" name="coordinator" /> Grant coordinator access
        </label>
        <div className="sm:col-span-2">
          <button type="submit" className="btn-primary" disabled={add.isPending}>Add staff member</button>
        </div>
      </form>

      <ul className="space-y-2">
        {data?.staff.map((s) => (
          <li key={s.id} className="card flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium">{s.name ?? s.email}</p>
              <p className="text-sm text-uoa-grey">
                {s.email}
                {s.staffProfile?.department ? ` · ${s.staffProfile.department}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <StatusPill status={s.role} />
              <button className="btn-secondary" onClick={() => remove.mutate(s.id)}>Remove</button>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function Housekeeping() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "stale"],
    queryFn: () => api.get<{ cutoff: string; users: any[] }>("/admin/stale-students"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/admin/students/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "stale"] }),
  });

  if (isLoading) return <Spinner />;
  const users = data?.users ?? [];

  return (
    <>
      <Alert>
        Students with no sign-in since {data && new Date(data.cutoff).toLocaleDateString("en-NZ")}. Removing a student
        permanently deletes their profile, CV, applications and reference requests.
      </Alert>
      {users.length === 0 ? (
        <Empty>No stale student accounts.</Empty>
      ) : (
        <ul className="space-y-2">
          {users.map((u) => (
            <li key={u.id} className="card flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium">{u.name ?? u.email}</p>
                <p className="text-sm text-uoa-grey">
                  Last sign-in: {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString("en-NZ") : "never"}
                </p>
              </div>
              <button className="btn-secondary" onClick={() => remove.mutate(u.id)}>Delete</button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Audit() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "audit"],
    queryFn: () => api.get<{ events: any[] }>("/admin/audit?limit=200"),
  });
  if (isLoading) return <Spinner />;

  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">Recent audit events</caption>
        <thead className="bg-uoa-sand">
          <tr>
            <th scope="col" className="px-4 py-3 font-semibold">When</th>
            <th scope="col" className="px-4 py-3 font-semibold">Who</th>
            <th scope="col" className="px-4 py-3 font-semibold">Action</th>
            <th scope="col" className="px-4 py-3 font-semibold">Target</th>
          </tr>
        </thead>
        <tbody>
          {data?.events.map((e) => (
            <tr key={e.id} className="border-t border-line">
              <td className="whitespace-nowrap px-4 py-2">{new Date(e.createdAt).toLocaleString("en-NZ")}</td>
              <td className="px-4 py-2">{e.actor?.email ?? "—"}</td>
              <td className="px-4 py-2 font-mono text-xs">{e.action}</td>
              <td className="px-4 py-2 font-mono text-xs text-uoa-grey">{e.targetType ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
